/**
 * WorkItemBoardColumn — Kanban Board column.
 *
 * Features:
 *   - Sticky header with colored dot + label + count badge
 *   - WIP limit indicator ("7 / 10") with red + AlertTriangle when at/over limit (LEAN-01)
 *   - Independent vertical scroll with hidden scrollbar (single-scroll-container rule)
 *   - Drop hover: brightened background + colored ring
 *   - Invalid drop target: no ring, cursor-not-allowed
 *   - Released column: collapsible 7-day filter with "Show all" link
 *   - Exceptions column: two sub-headers (Blocked / Escalated) each with count
 *   - Empty state: muted "No items" text, drop zone stays active
 *
 * The parent (WorkItemBoard) passes `--status-accent` via inline style on the
 * column wrapper, so the header dot, top bar, and card accent strip all use
 * the same token without duplication.
 */

import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '@/lib/utils';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import type { RorosWorkItemDTO } from '@/types/roros/control-tower';
import type { RorosWipStatusDTO } from '@/types/roros/control-tower';
import { WorkItemBoardCard } from './WorkItemBoardCard';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface ColumnConfig {
  id: string;               // 'backlog' | 'ready' | 'in_progress' | 'done' | 'quality_confirmation' | 'released' | 'exceptions'
  label: string;
  statusAccent: string;     // e.g. 'var(--color-info-500)'
  statuses: string[];       // underlying status values in this column
}

interface WorkItemBoardColumnProps {
  config: ColumnConfig;
  items: RorosWorkItemDTO[];
  wipData: RorosWipStatusDTO | undefined;
  /** Whether the column is a valid drop target for the active drag. */
  isValidDropTarget: boolean;
  canEdit: boolean;
  onOpenDrawer: (item: RorosWorkItemDTO) => void;
  actorName: (id: string | null) => string;
  domainName: (id: string) => string;
  /** For Released column: whether to show only last-7-days items. */
  showReleasedAll?: boolean;
  onToggleReleasedAll?: () => void;
}

// ─── WorkItemBoardColumn ───────────────────────────────────────────────────

export function WorkItemBoardColumn({
  config,
  items,
  wipData,
  isValidDropTarget,
  canEdit,
  onOpenDrawer,
  actorName,
  domainName,
  showReleasedAll = false,
  onToggleReleasedAll,
}: WorkItemBoardColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: config.id,
    data: { columnId: config.id, statuses: config.statuses },
  });

  // Exceptions sub-section collapse state
  const [blockedCollapsed, setBlockedCollapsed] = useState(false);
  const [escalatedCollapsed, setEscalatedCollapsed] = useState(false);

  const isExceptions = config.id === 'exceptions';
  const isReleased = config.id === 'released';

  // WIP limit for this column (only non-exception columns have a numeric limit)
  const primaryStatus = config.statuses[0];
  const count = items.length;
  const limit: number | null = wipData?.limits?.[primaryStatus] ?? null;
  const atOrOverLimit = limit !== null && count >= limit;

  // Drop visual state
  const canDrop = isValidDropTarget;
  const showDropRing = isOver && canDrop;
  const showNotAllowed = isOver && !canDrop;

  return (
    <div
      className={cn(
        'flex flex-col min-w-72 flex-1 rounded-lg border border-border bg-background/60 transition-colors duration-150',
        showDropRing && 'ring-2 ring-[var(--status-accent)] bg-[color-mix(in_srgb,var(--status-accent)_8%,transparent)]',
        showNotAllowed && 'cursor-not-allowed opacity-70',
      )}
      style={{ '--status-accent': config.statusAccent } as React.CSSProperties}
      aria-label={`${config.label} ${t('column with')} ${count} ${t('items')}`}
      data-testid={`board-column-${config.id}`}
    >
      {/* Top accent bar */}
      <div
        className="h-0.5 rounded-t-lg w-full"
        style={{ background: 'var(--status-accent)' }}
      />

      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm px-3 py-2.5 border-b border-border/50">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {/* Colored dot */}
            <span
              className="h-2 w-2 rounded-full flex-shrink-0"
              style={{ background: 'var(--status-accent)' }}
              aria-hidden="true"
            />
            <span className="text-xs font-semibold text-foreground truncate">
              {config.label}
            </span>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Count badge */}
            <span
              className="text-xs font-medium text-muted-foreground bg-muted rounded-full px-1.5 py-0.5 leading-none"
              data-testid={`board-column-count-${config.id}`}
            >
              {count}
            </span>

            {/* WIP indicator */}
            {limit !== null && !isExceptions && (
              <span
                className={cn(
                  'text-xs font-medium flex items-center gap-0.5 leading-none',
                  atOrOverLimit ? 'text-error-600' : 'text-muted-foreground',
                )}
                title={`${t('WIP')}: ${count} / ${limit}`}
                data-testid={`board-column-wip-${config.id}`}
              >
                {atOrOverLimit && (
                  <AlertTriangle className="h-3 w-3 text-error-500" aria-label={t('WIP limit reached')} />
                )}
                <span>{count} / {limit}</span>
              </span>
            )}
          </div>
        </div>

        {/* Released: "Show all" / "Show last 7 days" toggle */}
        {isReleased && onToggleReleasedAll && (
          <Button
            variant="link"
            size="sm"
            onClick={onToggleReleasedAll}
            className="mt-1 h-auto p-0 text-[10px]"
            data-testid="released-toggle-all"
          >
            {showReleasedAll ? t('Show last 7 days') : t('Show all')}
          </Button>
        )}
      </div>

      {/* Column body — independent vertical scroll (Fix 2: scrollbar visible so users know there's more content) */}
      <div
        ref={setNodeRef}
        className={cn(
          'flex-1 overflow-y-auto p-2 space-y-2',
          showNotAllowed && 'pointer-events-none',
        )}
        tabIndex={0}
        aria-label={`${config.label} ${t('drop zone')}`}
      >
        {isExceptions ? (
          <ExceptionsColumnBody
            items={items}
            canEdit={canEdit}
            onOpenDrawer={onOpenDrawer}
            actorName={actorName}
            domainName={domainName}
            blockedCollapsed={blockedCollapsed}
            escalatedCollapsed={escalatedCollapsed}
            onToggleBlocked={() => setBlockedCollapsed((v) => !v)}
            onToggleEscalated={() => setEscalatedCollapsed((v) => !v)}
          />
        ) : (
          <>
            {items.length === 0 ? (
              <div
                className="flex items-center justify-center py-6 text-xs text-muted-foreground/60"
                data-testid={`board-column-empty-${config.id}`}
              >
                {t('No items')}
              </div>
            ) : (
              items.map((item) => (
                <WorkItemBoardCard
                  key={item.id}
                  item={item}
                  canEdit={canEdit}
                  onOpenDrawer={onOpenDrawer}
                  actorName={actorName}
                  domainName={domainName}
                />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── ExceptionsColumnBody ─────────────────────────────────────────────────

function ExceptionsColumnBody({
  items,
  canEdit,
  onOpenDrawer,
  actorName,
  domainName,
  blockedCollapsed,
  escalatedCollapsed,
  onToggleBlocked,
  onToggleEscalated,
}: {
  items: RorosWorkItemDTO[];
  canEdit: boolean;
  onOpenDrawer: (item: RorosWorkItemDTO) => void;
  actorName: (id: string | null) => string;
  domainName: (id: string) => string;
  blockedCollapsed: boolean;
  escalatedCollapsed: boolean;
  onToggleBlocked: () => void;
  onToggleEscalated: () => void;
}) {
  const blocked = items.filter((i) => i.status === 'blocked');
  const escalated = items.filter((i) => i.status === 'escalated');

  return (
    <div className="space-y-1">
      {/* Blocked sub-section */}
      <ExceptionsSubSection
        label={t('Blocked')}
        items={blocked}
        collapsed={blockedCollapsed}
        onToggle={onToggleBlocked}
        canEdit={canEdit}
        onOpenDrawer={onOpenDrawer}
        actorName={actorName}
        domainName={domainName}
        testId="blocked"
      />

      {/* Escalated sub-section */}
      <ExceptionsSubSection
        label={t('Escalated')}
        items={escalated}
        collapsed={escalatedCollapsed}
        onToggle={onToggleEscalated}
        canEdit={canEdit}
        onOpenDrawer={onOpenDrawer}
        actorName={actorName}
        domainName={domainName}
        testId="escalated"
      />

      {blocked.length === 0 && escalated.length === 0 && (
        <div className="flex items-center justify-center py-6 text-xs text-muted-foreground/60">
          {t('No items')}
        </div>
      )}
    </div>
  );
}

function ExceptionsSubSection({
  label,
  items,
  collapsed,
  onToggle,
  canEdit,
  onOpenDrawer,
  actorName,
  domainName,
  testId,
}: {
  label: string;
  items: RorosWorkItemDTO[];
  collapsed: boolean;
  onToggle: () => void;
  canEdit: boolean;
  onOpenDrawer: (item: RorosWorkItemDTO) => void;
  actorName: (id: string | null) => string;
  domainName: (id: string) => string;
  testId: string;
}) {
  return (
    <div>
      <Button
        variant="ghost"
        size="sm"
        className="flex items-center gap-1 w-full py-1 px-0.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors h-auto justify-start"
        onClick={onToggle}
        aria-expanded={!collapsed}
        data-testid={`exceptions-subsection-${testId}`}
      >
        {collapsed ? (
          <ChevronRight className="h-3 w-3 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-3 w-3 flex-shrink-0" />
        )}
        <span>{label}</span>
        <span className="ml-auto bg-muted rounded-full px-1.5 py-0.5 text-[10px] leading-none">
          {items.length}
        </span>
      </Button>

      {!collapsed && (
        <div className="mt-1 space-y-2 pl-1">
          {items.length === 0 ? (
            <div className="text-xs text-muted-foreground/50 py-2 px-1">
              {t('No items')}
            </div>
          ) : (
            items.map((item) => (
              <WorkItemBoardCard
                key={item.id}
                item={item}
                canEdit={canEdit}
                onOpenDrawer={onOpenDrawer}
                actorName={actorName}
                domainName={domainName}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
